import React, { useCallback, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { organizationsAPI } from '../api/organizations'
import { API_ENDPOINTS } from '../config/api'
import { downloadExport } from '../utils/downloadExport'
import type { Organization, OrganizationMember, OrganizationInvitation, OrganizationSurvey } from '../api/organizations'
import {
  Button,
  Card,
  CardHeader,
  PageLayout,
  PageHeader,
  Alert,
  Input,
  Select,
  StatusBadge,
} from '../components/ui'
import { MemberTable } from '../components/MemberTable'
import { InvitationTable } from '../components/InvitationTable'
import { OverdueSection } from '../components/OverdueSection'
import { ApiKeyTable } from '../components/ApiKeyTable'
import { CustomDomainSection } from '../components/CustomDomainSection'

type TabKey = 'members' | 'surveys' | 'invitations' | 'api-keys' | 'settings'

const TAB_LABEL_IDS: Record<TabKey, string> = {
  members: 'org.tab.members',
  surveys: 'org.tab.surveys',
  invitations: 'org.tab.invitations',
  'api-keys': 'org.tab.apiKeys',
  settings: 'org.tab.settings',
}

const VALID_TABS: TabKey[] = ['members', 'surveys', 'invitations', 'api-keys', 'settings']

const OrganizationDetailPage: React.FC = () => {
  const intl = useIntl()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { locale } = useLanguage()
  const [searchParams, setSearchParams] = useSearchParams()


  const tabParam = searchParams.get('tab') as TabKey | null
  const activeTab: TabKey = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'members'

  const setActiveTab = (tab: TabKey) => {
    setSearchParams(tab === 'members' ? {} : { tab }, { replace: true })
  }

  const [organization, setOrganization] = useState<Organization | null>(null)
  const [members, setMembers] = useState<OrganizationMember[]>([])
  const [invitations, setInvitations] = useState<OrganizationInvitation[]>([])
  const [surveys, setSurveys] = useState<OrganizationSurvey[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [responseCount, setResponseCount] = useState(0)
  const [exportingMembers, setExportingMembers] = useState(false)
  const [membersExportError, setMembersExportError] = useState(false)

  const handleExportMembersCsv = async () => {
    if (!id) return
    setExportingMembers(true)
    setMembersExportError(false)
    try {
      await downloadExport(
        API_ENDPOINTS.organizations.membersExportCsv(id, locale),
        `${organization?.name || 'members'}.csv`,
      )
    } catch (err) {
      console.error('Members export failed:', err)
      setMembersExportError(true)
    } finally {
      setExportingMembers(false)
    }
  }

  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'RESPONDENT' | 'ADMIN'>('RESPONDENT')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)

  const loadOrganizationData = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      const [org, memberList, invitationList, surveyList] = await Promise.all([
        organizationsAPI.getOrganization(id),
        organizationsAPI.getMembers(id),
        organizationsAPI.getInvitations(id),
        organizationsAPI.getSurveys(id),
      ])
      setOrganization(org)
      setMembers(memberList)
      setInvitations(invitationList)
      setSurveys(surveyList)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : intl.formatMessage({ id: 'org.error.loadFailed' })
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [id, intl])

  useEffect(() => {
    if (id) {
      loadOrganizationData()
    }
  }, [id, loadOrganizationData])

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id || !inviteEmail) return
    try {
      setInviting(true)
      setInviteError(null)
      await organizationsAPI.inviteMember(id, { email: inviteEmail, role: inviteRole })
      setInviteEmail('')
      setInviteRole('RESPONDENT')
      setShowInviteForm(false)
      // Reload invitations and org stats
      const [invitationList, org] = await Promise.all([
        organizationsAPI.getInvitations(id),
        organizationsAPI.getOrganization(id),
      ])
      setInvitations(invitationList)
      setOrganization(org)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : intl.formatMessage({ id: 'org.error.inviteFailed' })
      setInviteError(message)
    } finally {
      setInviting(false)
    }
  }

  const handleRemoveMember = async (memberId: string) => {
    if (!id || !confirm(intl.formatMessage({ id: 'org.confirm.removeMember' }))) return
    try {
      await organizationsAPI.removeMember(id, memberId)
      const [memberList, org] = await Promise.all([
        organizationsAPI.getMembers(id),
        organizationsAPI.getOrganization(id),
      ])
      setMembers(memberList)
      setOrganization(org)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : intl.formatMessage({ id: 'org.error.removeFailed' })
      alert(message)
    }
  }

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    if (!id) return
    try {
      await organizationsAPI.updateMemberRole(id, memberId, newRole)
      const memberList = await organizationsAPI.getMembers(id)
      setMembers(memberList)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : intl.formatMessage({ id: 'org.error.updateRoleFailed' })
      alert(message)
    }
  }

  const handleCancelInvitation = async (invitationId: string) => {
    if (!id || !confirm(intl.formatMessage({ id: 'org.confirm.cancelInvitation' }))) return
    try {
      await organizationsAPI.cancelInvitation(id, invitationId)
      setInvitations(invitations.filter((i) => i.id !== invitationId))
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : intl.formatMessage({ id: 'org.error.cancelInviteFailed' })
      alert(message)
    }
  }

  const handleResendInvitation = async (invitation: OrganizationInvitation) => {
    if (!id) return
    try {
      await organizationsAPI.resendInvitation(id, invitation)
      const invitationList = await organizationsAPI.getInvitations(id)
      setInvitations(invitationList)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : intl.formatMessage({ id: 'org.error.resendInviteFailed' })
      alert(message)
    }
  }

  // Compute response count from surveys (must be before early returns to satisfy hooks rules)
  useEffect(() => {
    if (!surveys.length) { setResponseCount(0); return }
    const token = localStorage.getItem('access_token')
    if (!token) return
    Promise.all(
      surveys.map(s =>
        fetch(API_ENDPOINTS.surveys.responses(s.id), { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : [])
          .then(data => Array.isArray(data) ? data.length : 0)
          .catch(() => 0)
      )
    ).then(counts => setResponseCount(counts.reduce((a, b) => a + b, 0)))
  }, [surveys])

  if (loading) {
    return (
      <PageLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      </PageLayout>
    )
  }

  if (error || !organization) {
    return (
      <PageLayout>
        <Alert variant="error">{error || intl.formatMessage({ id: 'org.error.notFound' })}</Alert>
      </PageLayout>
    )
  }

  const currentUserMember = members.find((m) => m.user.email === user?.email)
  const isOwner = currentUserMember?.role === 'OWNER'
  const isOwnerOrAdmin = isOwner || currentUserMember?.role === 'ADMIN'

  // Respondents only see the Surveys tab
  const visibleTabs: TabKey[] = isOwnerOrAdmin
    ? VALID_TABS
    : ['surveys']

  // If activeTab is not in visibleTabs (e.g. respondent defaulting to 'members'), use first visible tab
  const effectiveTab: TabKey = visibleTabs.includes(activeTab) ? activeTab : visibleTabs[0]

  const surveyCount = surveys.length
  const memberCount = members.length
  const completionRate =
    surveyCount > 0 && memberCount > 0
      ? Math.round((responseCount / (surveyCount * memberCount)) * 100)
      : 0

  const statCards = [
    { labelId: 'org.stats.members', value: memberCount, color: 'bg-primary/10 text-primary-dark' },
    { labelId: 'org.stats.surveys', value: surveyCount, color: 'bg-secondary/10 text-secondary-dark' },
    { labelId: 'org.stats.responses', value: responseCount, color: 'bg-success-light text-success' },
    { labelId: 'org.stats.completionRate', value: `${completionRate}%`, color: 'bg-warning-light text-warning' },
  ]

  return (
    <PageLayout>
      <PageHeader
        title={organization.name}
        subtitle={organization.description || undefined}
        backTo="/organizations"
        backLabel={intl.formatMessage({ id: 'org.backToOrganizations' })}
        actions={
          isOwnerOrAdmin ? (
            <Button onClick={() => setShowInviteForm(true)}>
              <FormattedMessage id="org.inviteMember" />
            </Button>
          ) : undefined
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {statCards.map((stat) => (
          <Card key={stat.labelId} padding="sm">
            <div className={`rounded-lg p-4 ${stat.color}`}>
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="text-sm opacity-80">
                <FormattedMessage id={stat.labelId} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Overdue Assignments — only for OWNER/ADMIN */}
      {isOwnerOrAdmin && <OverdueSection orgId={id!} />}

      {/* Invite Member Form */}
      {showInviteForm && (
        <Card className="mb-6" elevated>
          <CardHeader
            title={intl.formatMessage({ id: 'org.invite.title' })}
            action={
              <Button variant="ghost" size="sm" onClick={() => setShowInviteForm(false)}>
                <FormattedMessage id="org.invite.cancel" />
              </Button>
            }
          />
          {inviteError && (
            <Alert variant="error" className="mb-4">
              {inviteError}
            </Alert>
          )}
          <form onSubmit={handleInviteMember} className="flex gap-4 items-end">
            <div className="flex-1">
              <Input
                label={intl.formatMessage({ id: 'org.invite.emailLabel' })}
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={intl.formatMessage({ id: 'org.invite.emailPlaceholder' })}
                required
              />
            </div>
            <div className="w-36">
              <Select
                label={intl.formatMessage({ id: 'org.invite.roleLabel' })}
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'RESPONDENT' | 'ADMIN')}
                options={[
                  { value: 'RESPONDENT', label: intl.formatMessage({ id: 'org.role.respondent' }) },
                  { value: 'ADMIN', label: intl.formatMessage({ id: 'org.role.admin' }) },
                ]}
              />
            </div>
            <Button type="submit" loading={inviting}>
              {inviting
                ? intl.formatMessage({ id: 'org.invite.sending' })
                : intl.formatMessage({ id: 'org.invite.send' })}
            </Button>
          </form>
        </Card>
      )}

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="flex gap-6">
          {visibleTabs.map((tab) => {
            const isActive = effectiveTab === tab
            let count = 0
            if (tab === 'members') count = members.length
            if (tab === 'surveys') count = surveys.length
            if (tab === 'invitations') count = invitations.length
            if (tab === 'api-keys') count = -1 // no count for api keys
            if (tab === 'settings') count = -1

            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-foreground-muted hover:text-foreground hover:border-border'
                }`}
              >
                <FormattedMessage id={TAB_LABEL_IDS[tab]} />
                {count >= 0 && (
                  <span
                    className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
                      isActive
                        ? 'bg-primary/20 text-primary-dark'
                        : 'bg-background-alt text-foreground-muted'
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <Card elevated>
        {effectiveTab === 'members' && (
          <>
            {isOwnerOrAdmin && (
              <div className="mb-4 flex flex-col items-end gap-2">
                <button
                  className="px-3 py-1.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-background disabled:opacity-50"
                  disabled={exportingMembers}
                  onClick={handleExportMembersCsv}
                >
                  {exportingMembers
                    ? intl.formatMessage({ id: 'common.exporting' })
                    : intl.formatMessage({ id: 'common.exportCsv' })}
                </button>
                {membersExportError && (
                  <p className="text-sm text-error"><FormattedMessage id="common.exportFailed" /></p>
                )}
              </div>
            )}
            <MemberTable
              members={members}
              isOwner={isOwner}
              isOwnerOrAdmin={isOwnerOrAdmin}
              onRemove={handleRemoveMember}
              onUpdateRole={handleUpdateRole}
            />
          </>
        )}

        {effectiveTab === 'surveys' && (
          <SurveyTable surveys={surveys} orgId={id!} canManage={isOwnerOrAdmin} />
        )}

        {effectiveTab === 'invitations' && (
          <InvitationTable
            invitations={invitations}
            isOwnerOrAdmin={isOwnerOrAdmin}
            onCancel={handleCancelInvitation}
            onResend={handleResendInvitation}
          />
        )}

        {effectiveTab === 'api-keys' && (
          <ApiKeyTable orgId={id!} />
        )}

        {effectiveTab === 'settings' && (
          <CustomDomainSection
            organization={organization}
            isOwner={isOwner}
            onUpdated={(org) => setOrganization(org)}
          />
        )}
      </Card>
    </PageLayout>
  )
}

// Inline survey table for the Surveys tab
const SurveyTable: React.FC<{ surveys: OrganizationSurvey[]; orgId: string; canManage: boolean }> = ({ surveys, orgId, canManage }) => {
  const navigate = useNavigate()

  if (surveys.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-foreground-muted mb-4">
          <FormattedMessage id="org.surveys.empty" />
        </p>
        {canManage && (
          <Button onClick={() => navigate(`/surveys/new?org=${orgId}`)}>
            <FormattedMessage id="org.surveys.create" />
          </Button>
        )}
      </div>
    )
  }

  return (
    <div>
      {canManage && (
        <div className="flex justify-end mb-4">
          <Button size="sm" onClick={() => navigate(`/surveys/new?org=${orgId}`)}>
            <FormattedMessage id="org.surveys.create" />
          </Button>
        </div>
      )}
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="org.surveys.colTitle" /></th>
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="org.surveys.colStatus" /></th>
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="org.surveys.colQuestions" /></th>
            <th className="pb-3 font-semibold text-foreground"><FormattedMessage id="org.surveys.colCreated" /></th>
            <th className="pb-3 font-semibold text-foreground text-right"><FormattedMessage id="org.surveys.colActions" /></th>
          </tr>
        </thead>
        <tbody>
          {surveys.map((survey) => (
            <tr key={survey.id} className="border-b border-border last:border-0">
              <td className="py-3 font-medium text-foreground">{survey.title}</td>
              <td className="py-3">
                <StatusBadge status={survey.status} />
              </td>
              <td className="py-3 text-foreground-muted">
                {survey.questions?.length ?? 0}
              </td>
              <td className="py-3 text-foreground-muted">
                {new Date(survey.created_at).toLocaleDateString()}
              </td>
              <td className="py-3 text-right">
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/surveys/${survey.id}/responses?org=${orgId}`)}
                  >
                    <FormattedMessage id="org.surveys.responses" />
                  </Button>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/surveys/${survey.id}/edit?org=${orgId}`)}
                    >
                      <FormattedMessage id="org.surveys.edit" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/surveys/${survey.id}/share?org=${orgId}`)}
                  >
                    <FormattedMessage id="org.surveys.share" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  )
}

export default OrganizationDetailPage
